import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Form, ListGroup, Modal } from 'react-bootstrap';
import { FiArrowLeft, FiCheck, FiX, FiMessageSquare, FiCamera } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { Loading } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { cleaningApi, memoApi, CleaningCheck } from '../api/client';

export const RoomDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const listId = parseInt(id || '0', 10);

  const { data: inspectionData, isLoading } = useQuery({
    queryKey: ['inspection', listId],
    queryFn: () => cleaningApi.getInspection(listId),
    enabled: !!listId,
  });

  const updateCheckMutation = useMutation({
    mutationFn: ({ checkId, checked }: { checkId: number; checked: boolean }) =>
      cleaningApi.updateCheck(checkId, checked),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection', listId] });
    },
  });

  const toggleCompleteMutation = useMutation({
    mutationFn: () => cleaningApi.toggleComplete(listId),
    onSuccess: (response) => {
      if (response.data.success) {
        queryClient.invalidateQueries({ queryKey: ['inspection', listId] });
        queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
        toast.success(response.data.message || '상태가 변경되었습니다.');
      }
    },
    onError: () => {
      toast.error('작업에 실패했습니다.');
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: () => {
      const inspection = inspectionData?.data.data?.inspection;
      if (!inspection) return Promise.reject('No inspection data');
      const date = new Date(inspection.date).toISOString().split('T')[0];
      const memoText = comment.trim() || (selectedImage ? '사진' : '');
      return memoApi.addCommentWithImage(date, inspection.roomId, memoText, selectedImage || undefined);
    },
    onSuccess: () => {
      toast.success('메모가 추가되었습니다.');
      setComment('');
      setSelectedImage(null);
      setImagePreview(null);
      setShowMemoModal(false);
    },
    onError: () => {
      toast.error('메모 추가에 실패했습니다.');
    },
  });

  // 이미지 선택 핸들러
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // 이미지 삭제 핸들러
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return <Loading fullScreen text="불러오는 중..." />;
  }

  const inspection = inspectionData?.data.data?.inspection;
  if (!inspection) {
    return (
      <div className="text-center py-5">
        <p>객실 정보를 찾을 수 없습니다.</p>
        <Button onClick={() => navigate(-1)}>뒤로 가기</Button>
      </div>
    );
  }

  const isMyRoom = inspection.userId === user?.id;

  return (
    <div className="app-container" style={{ paddingBottom: '100px' }}>
      {/* Header */}
      <header className="app-header">
        <button
          className="btn btn-link text-white p-0"
          onClick={() => navigate(-1)}
        >
          <FiArrowLeft size={24} />
        </button>
        <h1 className="header-title">
          {inspection.roomId}
        </h1>
        <div className="header-actions">
          {inspection.completed && (
            <span className="badge bg-success">완료</span>
          )}
          {inspection.canceled && (
            <span className="badge bg-danger">취소</span>
          )}
        </div>
      </header>

      <main className="main-content">
        {/* Status Info */}
        <div className="bg-white rounded-3 p-3 mb-3 shadow-sm">
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted">담당자</span>
            <span className="fw-bold">
              {inspection.user?.name || '미배정'}
              {isMyRoom && <span className="text-primary"> (나)</span>}
            </span>
          </div>
          {inspection.isJaesil && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <span className="text-muted">유형</span>
              <span className="badge bg-warning text-dark">재실 청소</span>
            </div>
          )}
        </div>

        {/* Checklist */}
        <div className="bg-white rounded-3 shadow-sm mb-3">
          <div className="p-3 border-bottom">
            <h6 className="mb-0">청소 체크리스트</h6>
          </div>
          <ListGroup variant="flush">
            {inspection.cleaningChecks?.map((check: CleaningCheck) => (
              <ListGroup.Item
                key={check.id}
                className="checklist-item"
                action={isMyRoom}
                onClick={() => {
                  if (isMyRoom && !inspection.completed) {
                    updateCheckMutation.mutate({
                      checkId: check.id,
                      checked: !check.checked,
                    });
                  }
                }}
              >
                <Form.Check
                  type="checkbox"
                  checked={check.checked}
                  onChange={() => {}}
                  disabled={!isMyRoom || inspection.completed}
                />
                <span className={`checklist-label ${check.checked ? 'text-decoration-line-through text-muted' : ''}`}>
                  {check.cleaningItem.itemName}
                </span>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>

        {/* Memo Button */}
        <Button
          variant="outline-secondary"
          className="w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
          onClick={() => setShowMemoModal(true)}
        >
          <FiMessageSquare />
          메모 추가
        </Button>
      </main>

      {/* Bottom Action Button */}
      {isMyRoom && !inspection.canceled && (
        <div
          className="position-fixed bottom-0 start-0 end-0 p-3 bg-white border-top"
          style={{ zIndex: 1000 }}
        >
          <Button
            variant={inspection.completed ? 'warning' : 'success'}
            size="lg"
            className="w-100"
            onClick={() => toggleCompleteMutation.mutate()}
            disabled={toggleCompleteMutation.isPending}
          >
            {inspection.completed ? (
              <>
                <FiX className="me-2" />
                완료 취소
              </>
            ) : (
              <>
                <FiCheck className="me-2" />
                청소 완료
              </>
            )}
          </Button>
        </div>
      )}

      {/* Memo Modal */}
      <Modal show={showMemoModal} onHide={() => setShowMemoModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>메모 추가</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="메모 내용을 입력하세요"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="mb-3"
          />

          {/* 이미지 미리보기 */}
          {imagePreview && (
            <div className="position-relative mb-3 d-inline-block">
              <img
                src={imagePreview}
                alt="미리보기"
                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
              />
              <Button
                variant="danger"
                size="sm"
                className="position-absolute top-0 end-0 rounded-circle"
                style={{ transform: 'translate(30%, -30%)' }}
                onClick={handleRemoveImage}
              >
                <FiX />
              </Button>
            </div>
          )}

          {/* 카메라 촬영 버튼 */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            capture="environment"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
          <Button
            variant="outline-primary"
            className="w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={() => fileInputRef.current?.click()}
          >
            <FiCamera />
            사진 촬영
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMemoModal(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={() => addCommentMutation.mutate()}
            disabled={(!comment.trim() && !selectedImage) || addCommentMutation.isPending}
          >
            저장
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
